'use client';

/**
 * ④-1(2026-09-27 · FINAL_RUN §0-2·§2-4) — 광고 스크립트 게이트.
 *
 * 예전: 루트 layout <head> 에 adsbygoogle 로더가 «정적» 으로 박혀 전 페이지에서 떴다
 *       (+ 현장 상세에서만 자동광고 요청을 멈추는 인라인 스크립트). 운영 실측상 홈·상세 LCP·CLS 비용의 거의 전부.
 * 지금: 서버가 app_config ads/display_routes 를 읽어(캐시 300초 — /api/ads/display-routes) 여기로 넘기고,
 *       usePathname() 으로 라우트 클래스를 판정해 ON 일 때만 로더 <script> 를 <head> 에 넣는다.
 *       OFF 라우트의 DOM 에는 광고 스크립트 태그가 «아예 없다». 서버 HTML 은 모든 방문자·페이지에 동일.
 *
 * ⚠️ 한 번 넣은 로더는 뺄 수 없다 — ON 라우트에서 OFF 라우트로 «클라이언트 이동» 하면 이미 뜬 광고는 남는다.
 *    유료 착지(현장 상세)는 «직접 진입» 이라 주 경로는 막힌다.
 * ⚠️ 설정을 못 읽으면(네트워크·5xx) 코드 기본값(§0-2)으로 판정한다.
 * 유료 분양광고(시군구 배너 등 data-ad-paid="1")가 그려진 페이지에는 AdSense 로더를 넣지 않는다 — 결제한 광고 옆에 남의 광고를 붙이지 않는다.
 *   (서버 HTML 은 그대로 · 판정은 이미 그려진 DOM 으로만 — 블로그 상세 등 페이지 코드는 건드리지 않는다)
 */
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  ADSENSE_LOADER_SRC, DEFAULT_DISPLAY_ROUTES, DISPLAY_ROUTES_TTL_S, adRouteClass, normalizeDisplayRoutes,
  type DisplayRoutes,
} from '@/lib/ads/display-routes';

const SS_KEY = 'kd_ads_routes_v1';
const LOADER_ID = 'kd-adsense-loader';

let inflight: Promise<DisplayRoutes> | null = null;

function loadRoutes(): Promise<DisplayRoutes> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      if (raw) {
        const j = JSON.parse(raw) as { at: number; routes: unknown };
        if (j && typeof j.at === 'number' && Date.now() - j.at < DISPLAY_ROUTES_TTL_S * 1000) {
          return normalizeDisplayRoutes(j.routes);
        }
      }
    } catch { /* 저장 차단 */ }
    try {
      const r = await fetch('/api/ads/display-routes', { credentials: 'omit' });
      if (!r.ok) throw new Error(String(r.status));
      const j = await r.json();
      const routes = normalizeDisplayRoutes(j?.routes);
      try { sessionStorage.setItem(SS_KEY, JSON.stringify({ at: Date.now(), routes })); } catch { /* */ }
      return routes;
    } catch {
      return { ...DEFAULT_DISPLAY_ROUTES };
    }
  })();
  return inflight;
}

/** 이 문서에 유료 분양광고가 그려져 있는가 */
export function hasPaidKadeoraAd(doc: Pick<Document, 'querySelector'>): boolean {
  try { return !!doc.querySelector('[data-ad-paid="1"]'); } catch { return false; }
}

function injectLoader() {
  if (document.getElementById(LOADER_ID)) return;
  const s = document.createElement('script');
  s.id = LOADER_ID;
  s.async = true;
  s.src = ADSENSE_LOADER_SRC;
  s.crossOrigin = 'anonymous';
  document.head.appendChild(s);
}

export default function AdsGate() {
  const pathname = usePathname() ?? '/';
  const [routes, setRoutes] = useState<DisplayRoutes | null>(null);

  useEffect(() => {
    let alive = true;
    loadRoutes().then((r) => { if (alive) setRoutes(r); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!routes) return;
    if (routes[adRouteClass(pathname)] && !hasPaidKadeoraAd(document)) injectLoader();
  }, [routes, pathname]);

  return null;
}
